import React from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "../components/ui/breadcrumb"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

export function BreadcrumbRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("breadcrumb")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const items = element.children ?? []

  return (
    <Breadcrumb
      className={cn(stripPresetConflicts(element.className))}
      data-testid={`breadcrumb-${element.id}`}
      data-atom-type="breadcrumb"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <BreadcrumbList>
        {items.map((child, i) => {
          const isLast = i === items.length - 1
          const label = child.text ? String(resolveSymbol(child.text) ?? "") : ""

          return (
            <React.Fragment key={child.id}>
              {i > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {child.type === "button" ? (
                  isLast ? (
                    <BreadcrumbPage>{label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink
                      render={
                        <button
                          type="button"
                          onClick={() => dispatchEvent(child.id, "tap", { value: label })}
                        />
                      }
                    >
                      {label}
                    </BreadcrumbLink>
                  )
                ) : (
                  <ElementRenderer element={child} />
                )}
              </BreadcrumbItem>
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
